import { cloneDeep } from "lodash";
import { useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";

import useDebounce from "app/hooks/useDebounce";

import {
  onBPReadingView,
  togglePatientRPMModal,
  onCommunicationLogChange,
  toggleFetchRPMPatientData,
  toggleCommunicationLogCancelModal,
  toggleCommunicationLogDeleteModal,
} from "app/store/patient/slice/patientSlice";
import {
  useSaveCommunicationLogMutation,
  useDeleteCommunicationLogMutation,
} from "app/store/patient/api/patientApi";
import { initialCommunicationLog } from "app/store/patient/slice/state/patientRPMState";

import { showErrorToast, showSuccessToast } from "utils/toast";

const useCommunicationLog = (props) => {
  const {
    communicationLog,
    bpReadingsList,
    isCommunicationLogModal,
    communicationLogAction,
    currentRpmRowData,
    fetchRPMPatientData,
    currentCommunicationLog,
    originalCommunicationLog,
    deletingCommunicationId,
    isCommunicationLogCancelModal,
    isCommunicationLogDeleteModal,
  } = useSelector((state) => state.patient);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [newCommunicationLog, setNewCommunicationLog] = useState(
    initialCommunicationLog
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (currentCommunicationLog) {
      const copy = cloneDeep(currentCommunicationLog);
      setNewCommunicationLog({ ...copy });
    }
  }, [currentCommunicationLog]);

  const debounceDispatch = useCallback(
    useDebounce((value) => {
      dispatch(onCommunicationLogChange(value));
    }, 500),
    []
  );

  const handleInputChange = async (updatedCommunicationLog) => {
    setNewCommunicationLog({ ...updatedCommunicationLog });
    debounceDispatch(updatedCommunicationLog);
  };

  const handleCancel = () => {
    dispatch(
      togglePatientRPMModal({
        type: "communicationLog",
        value: { open: true, action: "", data: {} },
      })
    );

    handleToggleCancelModal(false);
    handleToggleDeleteModal(false, null);
  };

  const handleClose = () => {
    dispatch(
      togglePatientRPMModal({
        type: "communicationLog",
        value: { open: false, action: "", data: {} },
      })
    );
  };

  const handleToggleCancelModal = (open) => {
    dispatch(toggleCommunicationLogCancelModal(open));
  };

  const handleToggleDeleteModal = (open, data) => {
    dispatch(toggleCommunicationLogDeleteModal({ open, data }));
  };

  const [saveCommunicationLog] = useSaveCommunicationLogMutation();

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        ...initialCommunicationLog,
        ...newCommunicationLog,
        patientId: currentRpmRowData?.id,
      };

      const response = await saveCommunicationLog({
        isEditAction: communicationLogAction === "Edit",
        patientId: payload?.patientId,
        communicationLog: payload,
      }).unwrap();

      if (response?.type === "Error") {
        showErrorToast(response?.message);
      } else {
        handleCancel();
        showSuccessToast(response?.message);
        dispatch(
          onBPReadingView(
            bpReadingsList?.filter(
              (bpReading) => bpReading?.patientId !== payload?.patientId
            )
          )
        );
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [deleteCommunicationLog] = useDeleteCommunicationLogMutation();

  const handleDelete = async () => {
    try {
      setIsDeleting(true);

      const response = await deleteCommunicationLog(
        deletingCommunicationId
      ).unwrap();

      if (response?.type === "Error") {
        showErrorToast(response?.message);
      } else {
        handleCancel();
        showSuccessToast(response?.message);
        await dispatch(toggleFetchRPMPatientData(true));
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setIsDeleting(false);
      dispatch(toggleFetchRPMPatientData(false));
    }
  };

  return {
    theme,
    dispatch,
    isDeleting,
    handleClose,
    handleCancel,
    handleSubmit,
    handleDelete,
    isSubmitting,
    bpReadingsList,
    communicationLog,
    handleInputChange,
    currentRpmRowData,
    newCommunicationLog,
    fetchRPMPatientData,
    setNewCommunicationLog,
    communicationLogAction,
    handleToggleCancelModal,
    handleToggleDeleteModal,
    isCommunicationLogModal,
    originalCommunicationLog,
    isCommunicationLogCancelModal,
    isCommunicationLogDeleteModal,
  };
};

export default useCommunicationLog;
